'use client';
import React from 'react';
import { motion, type Variants } from 'motion/react';
import { ecosystem } from '@/data/movio';
import { slideIndexById, slides, type SlideDef } from '@/lib/presentation/slides';
import { goTo, presentation, usePresentation } from '@/lib/presentation/store';
import { CTAButton } from '@/components/ui/Button';
import { StoryProgress } from './StoryProgress';
import { GrowthPanel } from './GrowthPanel';
import { releasePresentation } from './useSlideInput';

const EASE = [0.22, 1, 0.36, 1] as const;

const item: Variants = {
  in: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.8, ease: EASE } },
  out: { opacity: 0, y: -10, filter: 'blur(4px)', transition: { duration: 0.28, ease: 'easeIn' } },
};

const line: Variants = {
  in: { y: '0%', transition: { duration: 1, ease: EASE } },
  out: { y: '108%', transition: { duration: 0.32, ease: 'easeIn' } },
};

/** Ecosystem node → the scene that tells its story. */
const ECOSYSTEM_SLIDE: Record<string, string> = {
  mobility: 'industry-mobility',
  logistics: 'industry-logistics',
  'ai-voice': 'industry-ai-voice',
  services: 'industry-services',
  agri: 'industry-agri',
  future: 'industry-future',
  'super-app': 'super-app',
};

function EcosystemNav() {
  const focus = usePresentation((s) => s.focus);
  return (
    <motion.ul variants={item} className="v2-chips" aria-label="Businesses in the MOVIO ecosystem">
      {ecosystem.map((node) => {
        const target = slideIndexById[ECOSYSTEM_SLIDE[node.id]];
        return (
          <li key={node.id}>
            <button
              type="button"
              className="v2-chip v2-chip-button"
              data-focus={focus === node.id}
              title={node.description}
              onClick={() => goTo(target)}
              onMouseEnter={() => presentation.set({ focus: node.id })}
              onMouseLeave={() => presentation.set({ focus: null })}
              onFocus={() => presentation.set({ focus: node.id })}
              onBlur={() => presentation.set({ focus: null })}
            >
              {node.label}
            </button>
          </li>
        );
      })}
    </motion.ul>
  );
}

function Slide({ slide, index, active }: { slide: SlideDef; index: number; active: boolean }) {
  const transition = usePresentation((s) => s.transition);
  const isHero = index === 0;
  const Heading = isHero ? 'h1' : 'h2';
  // Tertiary motion: copy lands after the camera has done most of its travelling.
  const delay = isHero ? 0.55 : Math.max(0.35, transition * 0.55);

  return (
    <section
      id={slide.id}
      className="v2-slide"
      data-key={slide.key}
      data-active={active}
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${slides.length}: ${slide.label}`}
      aria-hidden={!active}
      inert={!active}
    >
      <div className="v2-container v2-slide-inner">
        <motion.div
          className={`v2-slide-copy ${isHero ? 'v2-slide-copy-hero' : ''}`}
          initial={false}
          animate={active ? 'in' : 'out'}
          variants={{
            in: { transition: { staggerChildren: 0.08, delayChildren: delay } },
            out: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
          }}
        >
          <motion.p variants={item} className="v2-eyebrow text-glow">
            {slide.eyebrow}
          </motion.p>
          <Heading className={isHero ? 'v2-display mt-5' : 'v2-slide-title mt-4'}>
            {slide.headline.map((text, i) => (
              <span key={text} className="block overflow-hidden pb-[0.08em]">
                <motion.span
                  variants={line}
                  className={`block ${
                    i === 1
                      ? 'bg-gradient-to-r from-white via-glow-soft to-glow bg-clip-text text-transparent'
                      : ''
                  }`}
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </Heading>
          {slide.tagline && (
            <motion.p variants={item} className="v2-slide-tagline text-glow">
              {slide.tagline}
            </motion.p>
          )}
          {slide.body && (
            <motion.p variants={item} className="v2-slide-body">
              {slide.body}
            </motion.p>
          )}

          {slide.key === 'ecosystem' && <EcosystemNav />}

          {slide.chips && slide.key !== 'ecosystem' && (
            <motion.ul variants={item} className="v2-chips">
              {slide.chips.map((c) => (
                <li key={c} className="v2-chip">
                  {c}
                </li>
              ))}
            </motion.ul>
          )}

          {slide.steps && slide.story && (
            <motion.div variants={item}>
              <StoryProgress index={index} steps={slide.steps} story={slide.story} />
            </motion.div>
          )}

          {slide.key === 'growth' && (
            <motion.div variants={item}>
              <GrowthPanel index={index} />
            </motion.div>
          )}

          {slide.ctas && (
            <motion.div variants={item} className="v2-slide-ctas">
              {slide.ctas.map((cta) => (
                <CTAButton
                  key={cta.href}
                  href={cta.href}
                  variant={cta.variant === 'ghost' ? 'ghost-dark' : 'primary'}
                  arrow={cta.variant !== 'ghost' || !cta.href.startsWith('#')}
                >
                  {cta.label}
                </CTAButton>
              ))}
            </motion.div>
          )}

          {slide.note && (
            <motion.p variants={item} className="v2-slide-note">
              {slide.note}
            </motion.p>
          )}

          {index === slides.length - 1 && (
            <motion.button
              variants={item}
              type="button"
              className="v2-continue"
              onClick={releasePresentation}
            >
              FAQs and contact
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
                <path
                  d="M8 3v9.5M4 8.5l4 4 4-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/** All 14 slides live in the DOM (crawlable, readable without JS); only the active one is interactive. */
export function SlideOverlay() {
  const index = usePresentation((s) => s.index);
  return (
    <div className="v2-slides">
      {slides.map((slide, i) => (
        <Slide key={slide.id} slide={slide} index={i} active={i === index} />
      ))}
    </div>
  );
}
